import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Image as ImageIcon, ClipboardList, Lock, Camera, FolderOpen, X, ChevronLeft, ChevronRight } from "lucide-react";

const HOURLY_ONLY = "hourly";
const PAYMENT_LABEL = { cash: "Cash", check: "Check", send_invoice: "Send Invoice" };
const DAY_LABEL = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function formatDayLabel(day) {
  if (!day?.date) return "";
  const d = new Date(day.date + "T00:00:00");
  const s = d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
  return day.is_today ? `Today · ${s}` : s;
}

export default function JobEntries() {
  const { user } = useAuth();
  const { job_photos_enabled } = useBranding();
  const navigate = useNavigate();
  const [week, setWeek] = useState(null);          // { week_start, week_end, today, days: [...] }
  const [selectedDate, setSelectedDate] = useState(null);
  const [categories, setCategories] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const commissionEligible = user?.default_pay_method && user.default_pay_method !== HOURLY_ONLY;
  const selectedDay = week?.days?.find((d) => d.date === selectedDate) || null;
  const items = selectedDay?.jobs || [];
  const isToday = selectedDay?.is_today === true;
  const dayLocked = selectedDay?.locked === true;

  function empty(initialCats = categories) {
    return {
      customer_name: "",
      job_description: "",
      invoice_number: "",
      invoice_amount: "",
      total_hours: "",
      deductions: Object.fromEntries((initialCats || []).map((c) => [c, 0])),
      final_walk_around: null,
      final_payment_method: "",
      check_number: "",
      notes: "",
      photo_paths: [],
      custom_field_values: {},
    };
  }

  const refreshWeek = async () => {
    const { data } = await api.get("/jobs/week");
    setWeek(data);
    // Preserve the currently selected date if still in range, otherwise default to today.
    setSelectedDate((prev) => {
      const stillValid = prev && data.days?.some((d) => d.date === prev);
      return stillValid ? prev : data.today;
    });
    return data;
  };

  useEffect(() => {
    if (!commissionEligible) return;
    (async () => {
      const [_, settingsRes, fieldsRes] = await Promise.all([
        refreshWeek(),
        api.get("/settings"),
        api.get("/settings/custom-job-fields").catch(() => ({ data: { fields: [] } })),
      ]);
      const cats = settingsRes.data?.allowable_deduction_categories || [];
      setCategories(cats);
      setCustomFields(fieldsRes.data?.fields || []);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commissionEligible]);

  if (!commissionEligible) {
    return (
      <div className="space-y-6" data-testid="jobs-not-applicable">
        <header>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Job Entries</p>
          <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">Not available</h1>
        </header>
        <Card className="rounded-2xl border-slate-200">
          <CardContent className="p-8 text-center space-y-3">
            <Lock className="h-10 w-10 text-slate-400 mx-auto" />
            <p className="text-slate-700 font-semibold">Job entries are for commission-based roles only.</p>
            <p className="text-sm text-slate-500">Your current pay method is hourly only. If this is wrong, contact your admin.</p>
            <Button onClick={() => navigate("/dashboard")} className="bg-sky-600 hover:bg-sky-700 mt-2">Back to dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const openNew = () => {
    setEditing(null);
    setForm(empty(categories));
    setOpen(true);
  };
  const openEdit = (j) => {
    const prefilled = Object.fromEntries(categories.map((c) => [c, j.deductions?.[c] ?? 0]));
    // include any extra historical categories not in current list (read-only)
    for (const [k, v] of Object.entries(j.deductions || {})) {
      if (!(k in prefilled)) prefilled[k] = v;
    }
    setEditing(j);
    setForm({
      ...empty(categories),
      ...j,
      invoice_amount: j.invoice_amount ?? "",
      total_hours: j.total_hours ?? "",
      deductions: prefilled,
      final_walk_around: typeof j.final_walk_around === "boolean" ? j.final_walk_around : null,
      final_payment_method: j.final_payment_method || "",
      check_number: j.check_number || "",
      custom_field_values: j.custom_field_values || {},
    });
    setOpen(true);
  };

  const totalDed = Object.values(form.deductions || {}).reduce((s, v) => s + (Number(v) || 0), 0);

  const save = async () => {
    if (!form.customer_name?.trim()) { toast.error("Customer name is required"); return; }
    const invAmt = Number(form.invoice_amount);
    if (!invAmt || invAmt <= 0) { toast.error("Invoice amount is required and must be greater than 0"); return; }
    const hrs = Number(form.total_hours);
    if (!hrs || hrs <= 0) { toast.error("Total hours on job is required and must be greater than 0"); return; }
    if (form.final_walk_around === null) { toast.error("Please answer 'Final Walk Around Inspection Completed?'"); return; }
    if (!form.final_payment_method) { toast.error("Please select a final payment method"); return; }
    if (form.final_payment_method === "check" && !(form.check_number || "").trim()) {
      toast.error("Check number is required when payment is by check.");
      return;
    }

    // Custom field required validation
    const cfv = form.custom_field_values || {};
    for (const f of customFields) {
      if (!f.required) continue;
      const v = cfv[f.id];
      const isEmpty = v === undefined || v === null || v === "" || (f.type === "checkbox" && v === false);
      if (isEmpty) { toast.error(`"${f.label}" is required`); return; }
    }

    setBusy(true);
    try {
      const cleanDeductions = Object.fromEntries(
        Object.entries(form.deductions || {}).map(([k, v]) => [k, Number(v) || 0])
      );
      const payload = {
        customer_name: form.customer_name.trim(),
        job_description: form.job_description || "",
        invoice_number: form.invoice_number || "",
        invoice_amount: invAmt,
        total_hours: hrs,
        deductions: cleanDeductions,
        final_walk_around: !!form.final_walk_around,
        final_payment_method: form.final_payment_method,
        check_number: form.final_payment_method === "check" ? (form.check_number || "").trim() : "",
        notes: form.notes || "",
        photo_paths: form.photo_paths || [],
        custom_field_values: cfv,
      };
      if (editing) {
        await api.put(`/jobs/${editing.id}`, payload);
        toast.success("Job updated");
      } else {
        // Pin the new entry to the currently selected day so techs can
        // backfill jobs earlier in the week (e.g. yesterday). Backend
        // enforces same-week + not-approved rules.
        if (selectedDate) payload.date = selectedDate;
        await api.post("/jobs", payload);
        toast.success("Job added");
      }
      setOpen(false);
      await refreshWeek();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this job entry?")) return;
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Deleted");
      await refreshWeek();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_paths: [...(f.photo_paths || []), data.path] }));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const totals = items.reduce(
    (acc, j) => ({
      gross: acc.gross + Number(j.invoice_amount || 0),
      ded: acc.ded + Number(j.allowable_deduction || 0),
      hrs: acc.hrs + Number(j.total_hours || 0),
    }),
    { gross: 0, ded: 0, hrs: 0 }
  );

  return (
    <div className="space-y-4" data-testid="jobs-page">
      <header className="flex items-end justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Job Entries</p>
          <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">
            {isToday ? "Today's Jobs" : "This Week"}
          </h1>
          <p className="text-xs text-slate-500 mt-1" data-testid="jobs-day-summary">
            {selectedDay ? `${formatDayLabel(selectedDay)} · ${items.length} ${items.length === 1 ? "entry" : "entries"}` : "Loading…"}
            {isToday && ` · max 5 / day`}
          </p>
        </div>
        <Button
          onClick={openNew}
          disabled={dayLocked || items.length >= 5}
          className="bg-sky-600 hover:bg-sky-700 h-12 rounded-xl disabled:opacity-50"
          data-testid="add-job-btn"
          title={
            dayLocked
              ? "Day is locked — ask your admin to reopen it."
              : items.length >= 5
              ? "You've hit the 5-jobs-per-day cap."
              : undefined
          }
        >
          <Plus className="h-4 w-4 mr-1" /> Add Job
        </Button>
      </header>

      {/* -------- Week day-picker strip -------- */}
      {week && (
        <div className="flex items-stretch gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="week-day-strip">
          {week.days.map((d) => {
            const isSel = d.date === selectedDate;
            const chip = (
              <button
                key={d.date}
                type="button"
                onClick={() => setSelectedDate(d.date)}
                className={`min-w-[64px] flex-shrink-0 rounded-xl px-2.5 py-2 text-center transition-all border ${
                  isSel
                    ? "bg-sky-600 text-white border-sky-600 shadow-md"
                    : d.is_today
                    ? "bg-white text-sky-700 border-sky-300"
                    : "bg-white text-slate-700 border-slate-200 hover:border-sky-300"
                }`}
                data-testid={`week-day-${d.date}`}
              >
                <div className={`text-[10px] font-bold uppercase tracking-widest ${isSel ? "text-sky-100" : "text-slate-400"}`}>
                  {DAY_LABEL[d.weekday]}
                </div>
                <div className={`text-lg font-extrabold font-display leading-none mt-0.5 ${isSel ? "text-white" : ""}`}>
                  {String(new Date(d.date + "T00:00:00").getDate()).padStart(2, "0")}
                </div>
                <div className="mt-1 flex items-center justify-center gap-1">
                  {d.jobs.length > 0 && (
                    <span className={`text-[10px] font-bold ${isSel ? "text-sky-100" : "text-sky-700"}`}>
                      {d.jobs.length}
                    </span>
                  )}
                  {d.locked && <Lock className={`h-3 w-3 ${isSel ? "text-sky-100" : "text-amber-500"}`} />}
                </div>
              </button>
            );
            return chip;
          })}
        </div>
      )}

      {dayLocked && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="day-locked-banner">
          <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold">This day is closed.</p>
            <p className="text-xs">
              {selectedDay?.submission_status === "approved"
                ? "Your admin approved this day. Ask them to reopen it if you need to make a change."
                : "Payroll for this week has been finalized. Only your admin can adjust entries now."}
            </p>
          </div>
        </div>
      )}

      {!dayLocked && !isToday && selectedDay && (
        <div className="rounded-xl bg-sky-50 border border-sky-200 p-3 text-xs text-sky-900" data-testid="week-edit-banner">
          {selectedDay.submission_status === "submitted" || selectedDay.submission_status === "pending_approval" ? (
            <>You submitted this day — you can still <b>add, fix, or remove</b> jobs while your admin reviews it. Any change sends it back to the top of their review queue.</>
          ) : selectedDay.submission_status === "rejected" ? (
            <>Your admin rejected this day. <b>Add, fix, or remove jobs and it'll flip back to pending review</b> automatically.</>
          ) : (
            <>You can <b>add, edit, or delete</b> entries for this day since it's still in the current work week.</>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-300 rounded-2xl bg-white">
          <CardContent className="py-12 text-center">
            <ClipboardList className="h-10 w-10 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-700 font-semibold">
              {isToday ? "No job entries yet" : "No jobs logged on this day"}
            </p>
            <p className="text-sm text-slate-500">
              Add up to 5 jobs per day.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((j) => (
            <Card key={j.id} className="border-slate-200 rounded-2xl" data-testid={`job-card-${j.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-slate-900 truncate">{j.customer_name}</p>
                    <p className="text-sm text-slate-500 truncate">{j.job_description || j.invoice_number || "—"}</p>
                    {j.total_hours > 0 && (
                      <p className="text-xs text-slate-500 mt-1">{Number(j.total_hours).toFixed(2)}h on job</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-extrabold text-green-700">{fmtMoney(j.invoice_amount)}</p>
                    {Number(j.allowable_deduction) > 0 && (
                      <p className="text-xs text-red-600">-{fmtMoney(j.allowable_deduction)} ded.</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    {(j.photo_paths || []).length > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-500"><ImageIcon className="h-3.5 w-3.5" /> {j.photo_paths.length}</span>
                    )}
                    {typeof j.final_walk_around === "boolean" && (
                      <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${j.final_walk_around ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        Walk-around: {j.final_walk_around ? "Yes" : "No"}
                      </span>
                    )}
                    {j.final_payment_method && (
                      <span className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-sky-100 text-sky-700">
                        {PAYMENT_LABEL[j.final_payment_method] || j.final_payment_method}
                        {j.final_payment_method === "check" && j.check_number && (
                          <span className="ml-1 font-mono normal-case tracking-normal">#{j.check_number}</span>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(j)} disabled={dayLocked} data-testid={`edit-job-${j.id}-btn`}>Edit</Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(j.id)} disabled={dayLocked} className="text-red-600 disabled:opacity-40" data-testid={`delete-job-${j.id}-btn`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          <Card className="bg-slate-900 text-white rounded-2xl">
            <CardContent className="p-4 grid grid-cols-4 gap-3 text-center">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Hours</p>
                <p className="text-lg font-extrabold font-display">{totals.hrs.toFixed(2)}h</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Gross</p>
                <p className="text-lg font-extrabold font-display">{fmtMoney(totals.gross)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Deductions</p>
                <p className="text-lg font-extrabold font-display">{fmtMoney(totals.ded)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-green-400">Net</p>
                <p className="text-lg font-extrabold font-display text-green-400">{fmtMoney(totals.gross - totals.ded)}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="job-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Job Entry" : "New Job Entry"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Customer Name" required>
              <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="job-customer-input" />
            </Field>
            <Field label="Job Description">
              <Input value={form.job_description} onChange={(e) => setForm({ ...form, job_description: e.target.value })} data-testid="job-desc-input" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Invoice #">
                <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="job-invoice-num-input" />
              </Field>
              <Field label="Invoice Amount" required>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.invoice_amount}
                  onChange={(e) => setForm({ ...form, invoice_amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="job-amount-input"
                />
              </Field>
            </div>
            <Field label="Total Hours on Job" required>
              <Input
                type="number"
                step="0.25"
                min="0.25"
                value={form.total_hours}
                onChange={(e) => setForm({ ...form, total_hours: e.target.value })}
                placeholder="e.g., 2.5"
                data-testid="job-hours-input"
              />
            </Field>

            <div className="rounded-xl border border-slate-200 p-3 space-y-3 bg-slate-50/40">
              <div className="flex items-baseline justify-between">
                <h4 className="text-sm font-bold text-slate-900 font-display">Allowable Deductions</h4>
                <p className="text-xs text-slate-500">Total: <span className="font-bold text-slate-900">{fmtMoney(totalDed)}</span></p>
              </div>
              {Object.keys(form.deductions || {}).length === 0 && (
                <p className="text-xs text-slate-500">Your admin has not configured any deduction categories yet.</p>
              )}
              {Object.entries(form.deductions || {}).map(([cat, val]) => (
                <div key={cat} className="flex items-center gap-2">
                  <Label className="text-xs text-slate-700 flex-1 min-w-0 truncate" title={cat}>{cat}</Label>
                  <div className="relative w-28">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={val}
                      onChange={(e) => setForm({ ...form, deductions: { ...form.deductions, [cat]: e.target.value } })}
                      className="pl-6 h-9"
                      data-testid={`job-deduction-${cat.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}-input`}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 p-3 bg-slate-50/40">
              <Field label="Final Walk Around Inspection Completed?" required>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, final_walk_around: true })}
                    className={`h-10 rounded-lg border text-sm font-bold transition-colors ${
                      form.final_walk_around === true
                        ? "bg-green-600 text-white border-green-600"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                    }`}
                    data-testid="job-walkaround-yes-btn"
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, final_walk_around: false })}
                    className={`h-10 rounded-lg border text-sm font-bold transition-colors ${
                      form.final_walk_around === false
                        ? "bg-red-600 text-white border-red-600"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                    }`}
                    data-testid="job-walkaround-no-btn"
                  >
                    No
                  </button>
                </div>
              </Field>
              <Field label="Final Payment" required>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {[
                    { v: "cash", label: "Cash" },
                    { v: "check", label: "Check" },
                    { v: "send_invoice", label: "Send Invoice" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setForm({
                        ...form,
                        final_payment_method: opt.v,
                        // Clear stale check number when switching away
                        check_number: opt.v === "check" ? form.check_number : "",
                      })}
                      className={`h-10 rounded-lg border text-xs font-bold transition-colors ${
                        form.final_payment_method === opt.v
                          ? "bg-sky-600 text-white border-sky-600"
                          : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                      }`}
                      data-testid={`job-payment-${opt.v}-btn`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {form.final_payment_method === "check" && (
                  <div className="mt-2" data-testid="job-check-number-wrapper">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                      Check # <span className="text-red-600">*</span>
                    </label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={form.check_number || ""}
                      onChange={(e) => setForm({ ...form, check_number: e.target.value })}
                      placeholder="e.g. 1042"
                      className="mt-1"
                      data-testid="job-check-number-input"
                      autoFocus
                    />
                    <p className="text-[10px] text-slate-500 mt-1">Required — record the exact number printed on the check for payroll and audit.</p>
                  </div>
                )}
              </Field>
            </div>

            {/* Admin-defined custom fields */}
            {customFields.length > 0 && (
              <div className="space-y-3 border-t border-slate-200 pt-3">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-700">Verification</p>
                {customFields.map((f) => (
                  <CustomFieldInput
                    key={f.id}
                    field={f}
                    value={form.custom_field_values?.[f.id]}
                    onChange={(v) => setForm({
                      ...form,
                      custom_field_values: { ...(form.custom_field_values || {}), [f.id]: v },
                    })}
                  />
                ))}
              </div>
            )}

            <Field label="Notes">
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="job-notes-input" />
            </Field>
            {job_photos_enabled && (
              <Field label="Photos (optional)">
                {/* Hidden inputs — one bound to camera, one to gallery */}
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }}
                  className="hidden"
                  data-testid="job-photo-camera-input"
                />
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }}
                  className="hidden"
                  data-testid="job-photo-gallery-input"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => cameraInputRef.current?.click()}
                    disabled={uploading}
                    className="h-12 flex items-center justify-center gap-2"
                    data-testid="job-photo-camera-btn"
                  >
                    <Camera className="h-5 w-5" /> Take Photo
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => galleryInputRef.current?.click()}
                    disabled={uploading}
                    className="h-12 flex items-center justify-center gap-2"
                    data-testid="job-photo-gallery-btn"
                  >
                    <FolderOpen className="h-5 w-5" /> Choose from Gallery
                  </Button>
                </div>
                {uploading && <p className="text-xs text-slate-500 mt-2">Uploading…</p>}
                {(form.photo_paths || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="job-photo-thumbs">
                    {form.photo_paths.map((p, idx) => (
                      <div key={p} className="relative group">
                        <img
                          src={`${api.defaults.baseURL}/files/${p}`}
                          alt={`Job photo ${idx + 1}`}
                          className="h-20 w-20 object-cover rounded-lg border border-slate-200"
                        />
                        <button
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, photo_paths: f.photo_paths.filter((x) => x !== p) }))}
                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-600 text-white text-xs flex items-center justify-center shadow-md hover:bg-red-700"
                          data-testid={`job-photo-remove-${idx}`}
                          aria-label="Remove photo"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Field>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={save} disabled={busy} className="flex-1 bg-sky-600 hover:bg-sky-700" data-testid="save-job-btn">
                {busy ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function CustomFieldInput({ field, value, onChange }) {
  const testId = `cfield-${field.id}`;
  if (field.type === "radio_yes_no") {
    return (
      <Field label={field.label} required={field.required}>
        <div className="flex gap-2" data-testid={testId}>
          <Button
            type="button"
            variant={value === "yes" ? "default" : "outline"}
            onClick={() => onChange("yes")}
            className={value === "yes" ? "bg-green-600 hover:bg-green-700 flex-1" : "flex-1"}
            data-testid={`${testId}-yes`}
          >Yes</Button>
          <Button
            type="button"
            variant={value === "no" ? "default" : "outline"}
            onClick={() => onChange("no")}
            className={value === "no" ? "bg-red-600 hover:bg-red-700 flex-1" : "flex-1"}
            data-testid={`${testId}-no`}
          >No</Button>
        </div>
      </Field>
    );
  }
  if (field.type === "dropdown") {
    return (
      <Field label={field.label} required={field.required}>
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white"
          data-testid={testId}
        >
          <option value="">Select…</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </Field>
    );
  }
  if (field.type === "checkbox") {
    return (
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4"
          id={`cf-${field.id}`}
          data-testid={testId}
        />
        <label htmlFor={`cf-${field.id}`} className="text-sm text-slate-700">
          {field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      </div>
    );
  }
  if (field.type === "number") {
    return (
      <Field label={field.label} required={field.required}>
        <Input
          type="number"
          step="0.01"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          data-testid={testId}
        />
      </Field>
    );
  }
  // text (default)
  return (
    <Field label={field.label} required={field.required}>
      <Input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
    </Field>
  );
}
